/* eslint-disable no-param-reassign */
import orderBy from 'lodash/orderBy'
import range from 'lodash/range'
import uniqBy from 'lodash/uniqBy'
import { pipeWith } from 'pipe-ts'

type SimpleBlock = { colStart: number; colEnd: number; id: string | number }
type RichBlock<T extends SimpleBlock> = {
  block: T
  cols: number[]
  rowStart: number
  rowSpan: number
  overlappingGroups: number
}

export function arrangeBlocks<T extends SimpleBlock>(_blocks: T[]) {
  const arrangedBlocks = pipeWith(
    _blocks,
    function addArrangementInitialValues(blocks) {
      return blocks.map(block => ({
        block,
        cols: range(block.colStart, block.colEnd),
        rowStart: 0,
        rowSpan: 1,
        overlappingGroups: 0,
      }))
    },
    function toColumnsWithOccupyingBlocks(blocks) {
      return blocks.reduce(
        (colsOccupancy, block) => {
          block.cols.forEach(col => {
            colsOccupancy[col] = colsOccupancy[col] || []
            colsOccupancy[col].push(block)
          })
          return colsOccupancy
        },
        {} as { [k: number]: RichBlock<T>[] }
      )
    },
    function toEntries(_) {
      return Object.entries(_)
    },
    function updateOverlappingInfo(columns) {
      uniqBy(
        columns.filter(([, colBlocks]) => colBlocks.length > 1),
        ([, colBlocks]) =>
          orderBy(colBlocks, arrangedBlock => arrangedBlock.block.colStart)
            .map(arrangedBlock => arrangedBlock.block.id)
            .join(',')
      )
        .flatMap(([, blocks]) => blocks)
        .forEach(arrangedBlock => {
          arrangedBlock.overlappingGroups += 1
        })

      return columns
    },
    function orderByNumberOfBlocks(columns) {
      return orderBy(columns, ([, colBlocks]) => colBlocks.length, ['desc'])
    },
    _ =>
      new Set(
        _.flatMap(([, blocks]) => orderBy(blocks, block => block.overlappingGroups, ['desc']))
      ),
    _ => {
      const arrangedBlocksPerCol: {
        [k: string]: RichBlock<T>[]
      } = {}

      _.forEach(newBlock => {
        newBlock.cols.forEach(col => {
          arrangedBlocksPerCol[col] = arrangedBlocksPerCol[col] || []

          const colPositionedBlocks = arrangedBlocksPerCol[col]

          if (colPositionedBlocks.length === 1) {
            const [positionedBlock] = colPositionedBlocks
            if (positionedBlock.rowSpan === 1) {
              positionedBlock.rowSpan = 1 / 2
              positionedBlock.rowStart = 1 / 2
              newBlock.rowSpan = 1 / 2
              newBlock.rowStart = 0
            } else if (positionedBlock.rowSpan === 1 / 2) {
              newBlock.rowSpan = 1 / 2
              newBlock.rowStart = positionedBlock.rowStart === 0 ? 1 / 2 : 0
            } else if (positionedBlock.rowSpan === 1 / 3) {
              newBlock.rowSpan = 2 / 3
              newBlock.rowStart = positionedBlock.rowStart === 0 ? 1 / 3 : 0
            }
          } else if (colPositionedBlocks.length === 2) {
            newBlock.rowStart = 0
            newBlock.rowSpan = 1 / 3
            colPositionedBlocks.forEach((overlappingBlock, i) => {
              overlappingBlock.rowSpan = 1 / 3
              overlappingBlock.rowStart = (i + 1) / 3
            })
          }

          colPositionedBlocks.unshift(newBlock)
        })
      })

      return arrangedBlocksPerCol
    },
    _ => Array.from(new Set(Object.values(_).flat()))
  )

  return arrangedBlocks.map(({ block, rowStart, rowSpan }) => ({
    ...block,
    rowStart,
    rowSpan,
  }))
}
